import React, { useEffect, useRef, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { getToken } from '../services/authorize'
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import imageCompression from 'browser-image-compression';

const API = process.env.REACT_APP_API;
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const PHOTO_COMPRESS_OPTS = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true, fileType: 'image/jpeg' };
const MAX_PHOTO_UPLOAD_BYTES = 950 * 1024;

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:')) return path;
  return `${UPLOAD_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

// คำนำหน้าชื่อ → เพศ (เรียงจากยาวไปสั้นเพื่อให้จับคำที่เฉพาะเจาะจงก่อน)
const NAME_PREFIXES = [
  { prefix: 'เด็กหญิง', gen: 'f' },
  { prefix: 'เด็กชาย', gen: 'm' },
  { prefix: 'นางสาว', gen: 'f' },
  { prefix: 'ด.ช.', gen: 'm' },
  { prefix: 'ด.ญ.', gen: 'f' },
  { prefix: 'น.ส.', gen: 'f' },
  { prefix: 'ดช.', gen: 'm' },
  { prefix: 'ดญ.', gen: 'f' },
  { prefix: 'นส.', gen: 'f' },
  { prefix: 'นาย', gen: 'm' },
].sort((a, b) => b.prefix.length - a.prefix.length);

// บังคับให้มี space 1 ครั้งหลังคำนำหน้าชื่อ และคืนค่าเพศตามคำนำหน้า
const normalizeStudentName = (value) => {
  for (const { prefix, gen } of NAME_PREFIXES) {
    if (value.startsWith(prefix)) {
      const rest = value.slice(prefix.length);
      const name = rest.replace(/^\s+/, ''); // ตัด space ที่เกินออก
      let normalized;
      if (name.length > 0) {
        normalized = `${prefix} ${name}`;        // มีชื่อแล้ว → เว้น 1 space
      } else if (rest.length > 0) {
        normalized = `${prefix} `;               // เพิ่งเคาะ space ยังไม่พิมพ์ชื่อ
      } else {
        normalized = prefix;                     // พิมพ์แค่คำนำหน้า
      }
      return { value: normalized, gen };
    }
  }
  return { value, gen: null };
};
// ค่าเริ่มต้นของทุกช่อง — ใช้เป็นฐานตอนโหลดนักเรียนคนใหม่ ไม่ให้ค่าของคนก่อนหน้าค้างในฟอร์ม
const EMPTY_FORM = {
  st_idnumber: '',
  st_name: '',
  st_photo: '',
  st_gen: '',
  st_blood: '',
  st_idcard: '',
  st_birthdate: '',
  st_nationality: '',
  st_race: '',
  st_religion: '',
  st_childnumber: '',
  st_sibling: '',
  st_malesibling: '',
  st_femalesibling: '',
  st_homeID: '',
  st_oldaddress: '',
  st_oldSubDistrict: '',
  st_oldDistrict: '',
  st_oldProvince: '',
  st_oldZipcode: '',
  st_current: '',
  st_currentSubDistrict: '',
  st_currentDistrict: '',
  st_currentProvince: '',
  st_zipcode: '',
  st_fatherIdCard: '',
  st_fatherBirthdate: '',
  st_fatherName: '',
  st_fatherJob: '',
  st_fatherAnnualIncome: '',
  st_fatherAddress: '',
  st_fatherSubDistrict: '',
  st_fatherDistrict: '',
  st_fatherProvince: '',
  st_fatherZipcode: '',
  st_fatherPhone: '',
  st_motherIdCard: '',
  st_motherBirthdate: '',
  st_motherName: '',
  st_motherJob: '',
  st_motherAnnualIncome: '',
  st_motherAddress: '',
  st_motherSubDistrict: '',
  st_motherDistrict: '',
  st_motherProvince: '',
  st_motherZipcode: '',
  st_motherPhone: '',
  st_averageAnnualIncome: '',
  st_parentStatus: '',
  st_parent: '',
  st_parentRelation: '',
  st_parentIdCard: '',
  st_parentBirthdate: '',
  st_parentname: '',
  st_parentjob: '',
  st_parentannualincome: '',
  st_parentAddress: '',
  st_parentSubDistrict: '',
  st_parentDistrict: '',
  st_parentProvince: '',
  st_parentZipcode: '',
  st_parentPhone: '',
  st_educationsupporter: '',
  st_favoritesport: '',
  st_favoritesubject: '',
  st_futurestudy: '',
  st_futurejob: ''
};

export default function StudentInfoForm({ studentID, onSaved }) {
  const { setLoading } = useLoading();
  const photoInputRef = useRef(null);
  // Form state
  const [formData, setFormData] = useState({ ...EMPTY_FORM, st_id: studentID })
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Fetch student info when loading the component
  useEffect(() => {
    setLoading(true);

    axiosInstance.get(`${API}/getStudentInfo/${studentID}`, {
      headers: {
        Authorization: `Bearer ${getToken()}`
      }
    })
    .then(response => {
      setLoading(false);
      // console.log(response.data);
      const normalized = Object.fromEntries(
        Object.entries(response.data).map(([k, v]) => [k, v ?? ''])
      );
      // ตั้งจาก EMPTY_FORM ไม่ใช่ prev — หน้าที่สลับนักเรียนได้ในตัว (ค้นหาแล้วแก้)
      // ถ้าคนใหม่ไม่มีค่าในช่องไหน ค่าของคนก่อนหน้าจะค้างแล้วถูกบันทึกทับ
      setFormData({ ...EMPTY_FORM, ...normalized, st_id: studentID })
      setPhotoFile(null);
      if (photoInputRef.current) photoInputRef.current.value = "";
      setPhotoPreview(resolveUploadUrl(normalized.st_photo));
    })
    .catch(error => {
      setLoading(false)
      console.error("There was an error fetching the student info!", error)
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถดึงข้อมูลนักเรียนได้ กรุณาลองใหม่อีกครั้ง'
      })
    })
  }, [studentID, setLoading])

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    if (name === 'st_name') {
      const { value: normalized, gen } = normalizeStudentName(value)
      setFormData(prev => ({
        ...prev,
        st_name: normalized,
        ...(gen ? { st_gen: gen } : {})
      }))
      return
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'radio' ? value : value
    }))
  }

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      Swal.fire({ icon: 'warning', title: 'ไฟล์ไม่ถูกต้อง', text: 'กรุณาเลือกรูปภาพเท่านั้น' });
      return;
    }

    const applyPreparedPhoto = (preparedFile) => {
      if (preparedFile.size > MAX_PHOTO_UPLOAD_BYTES) {
        setPhotoFile(null);
        Swal.fire({
          icon: 'warning',
          title: 'รูปยังมีขนาดใหญ่เกินไป',
          text: 'กรุณาเลือกรูปที่เล็กลง หรือครอบ/ลดขนาดรูปก่อนอัปโหลด'
        });
        return;
      }

      setPhotoFile(preparedFile);
      setPhotoPreview(prev => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(preparedFile);
      });
    };

    setProcessingPhoto(true);
    try {
      const compressed = await imageCompression(file, PHOTO_COMPRESS_OPTS);
      const jpgFile = new File(
        [compressed],
        file.name.replace(/\.[^.]+$/, '') + '.jpg',
        { type: 'image/jpeg' }
      );

      applyPreparedPhoto(jpgFile);
    } catch (error) {
      console.warn('Student photo compression failed, using original file', error);
      applyPreparedPhoto(file);
    } finally {
      setProcessingPhoto(false);
    }
  }

  const handlePhotoUpload = async () => {
    if (!photoFile) return;

    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('studentPhoto', photoFile);

    try {
      const response = await axiosInstance.post(
        `${API}/uploadStudentPhoto/${studentID}`,
        fd,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
      const photoPath = response.data.photoPath || '';
      setFormData(prev => ({ ...prev, st_photo: photoPath }));
      setPhotoFile(null);
      setPhotoPreview(resolveUploadUrl(photoPath));
      if (photoInputRef.current) photoInputRef.current.value = '';
      Swal.fire({ icon: 'success', title: 'อัปโหลดรูปสำเร็จ', timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'อัปโหลดรูปไม่สำเร็จ',
        text: error.response?.data?.message || 'กรุณาลองใหม่อีกครั้ง'
      });
    } finally {
      setUploadingPhoto(false);
    }
  }

  // Handle parent selection (Dad, Mom, Other)
  const handleParentSelection = (parent) => {
    const newData = { ...formData, st_parent: parent };

    if (parent === 'dad') {
      newData.st_parentRelation = 'บิดา';
      newData.st_parentIdCard = formData.st_fatherIdCard;
      newData.st_parentBirthdate = formData.st_fatherBirthdate;
      newData.st_parentname = formData.st_fatherName;
      newData.st_parentjob = formData.st_fatherJob;
      newData.st_parentannualincome = formData.st_fatherAnnualIncome;
      newData.st_parentAddress = formData.st_fatherAddress;
      newData.st_parentSubDistrict = formData.st_fatherSubDistrict;
      newData.st_parentDistrict = formData.st_fatherDistrict;
      newData.st_parentProvince = formData.st_fatherProvince;
      newData.st_parentZipcode = formData.st_fatherZipcode;
      newData.st_educationsupporter = formData.st_fatherName;
    } else if (parent === 'mom') {
      newData.st_parentRelation = 'มารดา';
      newData.st_parentIdCard = formData.st_motherIdCard;
      newData.st_parentBirthdate = formData.st_motherBirthdate;
      newData.st_parentname = formData.st_motherName;
      newData.st_parentjob = formData.st_motherJob;
      newData.st_parentannualincome = formData.st_motherAnnualIncome;
      newData.st_parentAddress = formData.st_motherAddress;
      newData.st_parentSubDistrict = formData.st_motherSubDistrict;
      newData.st_parentDistrict = formData.st_motherDistrict;
      newData.st_parentProvince = formData.st_motherProvince;
      newData.st_parentZipcode = formData.st_motherZipcode;
      newData.st_educationsupporter = formData.st_motherName;
    } else if (parent === 'other') {
      newData.st_parentRelation = '';
      newData.st_parentIdCard = '';
      newData.st_parentBirthdate = '';
      newData.st_parentname = '';
      newData.st_parentjob = '';
      newData.st_parentannualincome = '';
      newData.st_parentAddress = '';
      newData.st_parentSubDistrict = '';
      newData.st_parentDistrict = '';
      newData.st_parentProvince = '';
      newData.st_parentZipcode = '';
      newData.st_educationsupporter = '';
    }

    setFormData(newData);
  }

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post(
        `${API}/updateStudentInfo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`
          }
        }
      );

      setLoading(false);
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'บันทึกข้อมูลนักเรียนเรียบร้อย'
      });
      // ให้หน้าที่ครอบอยู่รู้ว่าบันทึกแล้ว (หน้าค้นหาต้องอัปเดตชื่อในผลค้นหาให้ตรงกับที่แก้ไป)
      onSaved?.(formData);
    } catch (error) {
      setLoading(false);
      console.error("Error updating student info:", error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง'
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 mb-4">
        <div
          className="border bg-light d-flex align-items-center justify-content-center overflow-hidden"
          style={{ width: 'min(100%, 180px)', aspectRatio: '3 / 4', borderRadius: 8 }}
        >
          {photoPreview ? (
            <img
              src={photoPreview}
              alt={formData.st_name || 'student'}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div className="text-center text-secondary px-3">
              <i className="ri-user-line fs-1 d-block mb-1"></i>
              <span className="fs-sm">ยังไม่มีรูป</span>
            </div>
          )}
        </div>

        <div className="flex-grow-1 text-center text-md-start">
          <div className="fw-semibold mb-1">{formData.st_name || 'รูปนักเรียน'}</div>
          <div className="text-secondary fs-sm mb-3">{formData.st_idnumber}</div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="d-none"
            onChange={handlePhotoChange}
            disabled={processingPhoto || uploadingPhoto}
          />
          <div className="d-flex flex-column flex-sm-row gap-2">
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => photoInputRef.current?.click()}
              disabled={processingPhoto || uploadingPhoto}
            >
              <i className="ri-image-add-line me-1"></i>เลือกรูป
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handlePhotoUpload}
              disabled={!photoFile || processingPhoto || uploadingPhoto}
            >
              {processingPhoto || uploadingPhoto ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="ri-upload-cloud-line me-1"></i>}
              {processingPhoto ? 'กำลังเตรียมรูป' : 'อัปโหลดรูป'}
            </Button>
          </div>
        </div>
      </div>
    {/* ส่วนที่ 1: ข้อมูลส่วนตัว */}
      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสประจำตัว</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_idnumber"
            value={formData.st_idnumber}
            onChange={handleInputChange}
            placeholder="รหัสประจำตัวนักเรียน"
            disabled
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
      <Form.Label className="col-sm-2 text-end">ชื่อ-สกุล</Form.Label>
      <div className="col-sm-3">
        <Form.Control
          type="text"
          name="st_name"
          value={formData.st_name}
          onChange={handleInputChange}
          placeholder="ชื่อ-สกุล"
        />
      </div>
      <Form.Label className="col-sm-1 text-end">เพศ</Form.Label>
      <div className="col-sm-2">
        <Form.Check
          inline
          type="radio"
          label="ชาย"
          name="st_gen"
          value="m"
          checked={formData.st_gen === 'm'}
          onChange={handleInputChange}
        />
        <Form.Check
          inline
          type="radio"
          label="หญิง"
          name="st_gen"
          value="f"
          checked={formData.st_gen === 'f'}
          onChange={handleInputChange}
        />
      </div>
      <Form.Label className="col-sm-2 text-end">หมู่เลือด</Form.Label>
      <div className="col-sm-2">
        <Form.Control
          type="text"
          name="st_blood"
          value={formData.st_blood}
          onChange={handleInputChange}
          placeholder="หมู่เลือด"
        />
      </div>
      </Form.Group>

      <Form.Group className="row mb-3">
      <Form.Label className="col-sm-2 text-end">เลขประจำตัวประชาชน</Form.Label>
      <div className="col-sm-3">
        <Form.Control
          type="text"
          name="st_idcard"
          value={formData.st_idcard}
          onChange={handleInputChange}
          placeholder="เลขประจำตัวประชาชน"
          required
        />
      </div>
      <Form.Label className="col-sm-2 text-end">วัน เดือน ปี เกิด</Form.Label>
      <div className="col-sm-3">
        <Form.Control
          type="text"
          name="st_birthdate"
          value={formData.st_birthdate}
          onChange={handleInputChange}
          placeholder="เช่น 22/07/2550"
          required
        />
      </div>
    </Form.Group>

    <Form.Group className="row mb-3">
      <Form.Label className="col-sm-2 text-end">สัญชาติ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_nationality"
            value={formData.st_nationality}
            onChange={handleInputChange}
            placeholder="สัญชาติ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">เชื้อชาติ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_race"
            value={formData.st_race}
            onChange={handleInputChange}
            placeholder="เชื้อชาติ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ศาสนา</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_religion"
            value={formData.st_religion}
            onChange={handleInputChange}
            placeholder="ศาสนา"
          />
        </div>
      </Form.Group>

      {/* ส่วนที่ 2: ข้อมูลพี่น้อง */}
      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">เป็นบุตรคนที่</Form.Label>
        <div className="col-sm-1">
          <Form.Control
            type="number"
            name="st_childnumber"
            value={formData.st_childnumber}
            onChange={handleInputChange}
            placeholder="เป็นบุตรคนที่"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จำนวนพี่น้องร่วมสายโลหิต</Form.Label>
        <div className="col-sm-1">
          <Form.Control
            type="number"
            name="st_sibling"
            value={formData.st_sibling}
            onChange={handleInputChange}
            placeholder="จำนวนพี่น้อง"
          />
        </div>
        <Form.Label className="col-sm-1">คน</Form.Label>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-3">   </Form.Label>
        <Form.Label className="col-sm-2 text-end">ชาย</Form.Label>
        <div className="col-sm-1">
          <Form.Control
            type="number"
            name="st_malesibling"
            value={formData.st_malesibling}
            onChange={handleInputChange}
            placeholder="ชาย"
          />
        </div>
        <Form.Label className="col-sm-1">คน</Form.Label>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-3">   </Form.Label>
        <Form.Label className="col-sm-2 text-end">หญิง</Form.Label>
        <div className="col-sm-1">
          <Form.Control
            type="number"
            name="st_femalesibling"
            value={formData.st_femalesibling}
            onChange={handleInputChange}
            placeholder="หญิง"
          />
        </div>
        <Form.Label className="col-sm-1">คน</Form.Label>
      </Form.Group>

      {/* ส่วนที่ 3: ที่อยู่ */}
      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">เลขรหัสประจำบ้าน</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_homeID"
            value={formData.st_homeID}
            onChange={handleInputChange}
            placeholder="เลขรหัสประจำบ้าน"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ภูมิลำเนาเดิม</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            as="textarea"
            rows={3}
            name="st_oldaddress"
            value={formData.st_oldaddress}
            onChange={handleInputChange}
            placeholder="กรอกที่อยู่ภูมิบำเนาเดิม"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ตำบล</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_oldSubDistrict"
            value={formData.st_oldSubDistrict}
            onChange={handleInputChange}
            placeholder="ตำบลเดิม"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อำเภอ</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_oldDistrict"
            value={formData.st_oldDistrict}
            onChange={handleInputChange}
            placeholder="อำเภอเดิม"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จังหวัด</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_oldProvince"
            value={formData.st_oldProvince}
            onChange={handleInputChange}
            placeholder="จังหวัดเดิม"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสไปรษณีย์</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_oldZipcode"
            value={formData.st_oldZipcode}
            onChange={handleInputChange}
            placeholder="รหัสไปรษณีย์เดิม"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ที่อยู่ปัจจุบัน</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            as="textarea"
            rows={3}
            name="st_current"
            value={formData.st_current}
            onChange={handleInputChange}
            placeholder="กรอกที่อยู่ปัจจุบัน"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ตำบล</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_currentSubDistrict"
            value={formData.st_currentSubDistrict}
            onChange={handleInputChange}
            placeholder="ตำบลปัจจุบัน"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อำเภอ</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_currentDistrict"
            value={formData.st_currentDistrict}
            onChange={handleInputChange}
            placeholder="อำเภอปัจจุบัน"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จังหวัด</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_currentProvince"
            value={formData.st_currentProvince}
            onChange={handleInputChange}
            placeholder="จังหวัดปัจจุบัน"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสไปรษณีย์</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_zipcode"
            value={formData.st_zipcode}
            onChange={handleInputChange}
            placeholder="รหัสไปรษณีย์ปัจจุบัน"
          />
        </div>
      </Form.Group>

      {/* ส่วนที่ 4: ข้อมูลบิดา */}
      <hr className="my-4" />
      <h5 className="mb-3">ข้อมูลบิดา</h5>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">เลขประจำตัว</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherIdCard"
            value={formData.st_fatherIdCard}
            onChange={handleInputChange}
            placeholder="เลขประจำตัวประชาชนบิดา"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">วัน เดือน ปี เกิด</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_fatherBirthdate"
            value={formData.st_fatherBirthdate}
            onChange={handleInputChange}
            placeholder="เช่น 22/07/2550"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ชื่อ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_fatherName"
            value={formData.st_fatherName}
            onChange={handleInputChange}
            placeholder="กรอกชื่อบิดา"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">อาชีพ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_fatherJob"
            value={formData.st_fatherJob}
            onChange={handleInputChange}
            placeholder="ระบุอาชีพ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">รายได้/ปี</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="number"
            name="st_fatherAnnualIncome"
            value={formData.st_fatherAnnualIncome}
            onChange={handleInputChange}
            placeholder="รายได้ต่อปี"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ที่อยู่</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            as="textarea"
            rows={3}
            name="st_fatherAddress"
            value={formData.st_fatherAddress}
            onChange={handleInputChange}
            placeholder="กรอกที่อยู่"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ตำบล</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherSubDistrict"
            value={formData.st_fatherSubDistrict}
            onChange={handleInputChange}
            placeholder="ตำบล"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อำเภอ</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherDistrict"
            value={formData.st_fatherDistrict}
            onChange={handleInputChange}
            placeholder="อำเภอ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จังหวัด</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherProvince"
            value={formData.st_fatherProvince}
            onChange={handleInputChange}
            placeholder="จังหวัด"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสไปรษณีย์</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherZipcode"
            value={formData.st_fatherZipcode}
            onChange={handleInputChange}
            placeholder="รหัสไปรษณีย์"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">เบอร์โทร</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_fatherPhone"
            value={formData.st_fatherPhone}
            onChange={handleInputChange}
            placeholder="เบอร์โทรศัพท์"
          />
        </div>
      </Form.Group>

      {/* ส่วนที่ 5: ข้อมูลมารดา */}
      <hr className="my-4" />
      <h5 className="mb-3">ข้อมูลมารดา</h5>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">เลขประจำตัว</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherIdCard"
            value={formData.st_motherIdCard}
            onChange={handleInputChange}
            placeholder="เลขประจำตัวประชาชนมารดา"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">วัน เดือน ปี เกิด</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_motherBirthdate"
            value={formData.st_motherBirthdate}
            onChange={handleInputChange}
            placeholder="เช่น 22/07/2550"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ชื่อ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_motherName"
            value={formData.st_motherName}
            onChange={handleInputChange}
            placeholder="กรอกชื่อมารดา"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">อาชีพ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_motherJob"
            value={formData.st_motherJob}
            onChange={handleInputChange}
            placeholder="ระบุอาชีพ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">รายได้/ปี</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="number"
            name="st_motherAnnualIncome"
            value={formData.st_motherAnnualIncome}
            onChange={handleInputChange}
            placeholder="รายได้ต่อปี"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ที่อยู่</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            as="textarea"
            rows={3}
            name="st_motherAddress"
            value={formData.st_motherAddress}
            onChange={handleInputChange}
            placeholder="กรอกที่อยู่"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ตำบล</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherSubDistrict"
            value={formData.st_motherSubDistrict}
            onChange={handleInputChange}
            placeholder="ตำบล"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อำเภอ</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherDistrict"
            value={formData.st_motherDistrict}
            onChange={handleInputChange}
            placeholder="อำเภอ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จังหวัด</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherProvince"
            value={formData.st_motherProvince}
            onChange={handleInputChange}
            placeholder="จังหวัด"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสไปรษณีย์</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherZipcode"
            value={formData.st_motherZipcode}
            onChange={handleInputChange}
            placeholder="รหัสไปรษณีย์"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">เบอร์โทร</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_motherPhone"
            value={formData.st_motherPhone}
            onChange={handleInputChange}
            placeholder="เบอร์โทรศัพท์"
          />
        </div>
      </Form.Group>

      {/* ส่วนที่ 6: สถานะและผู้ปกครอง */}
      <hr className="my-4" />
      <h5 className="mb-3">สถานะและผู้ปกครอง</h5>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รายได้เฉลี่ยครอบครัว/ปี</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="number"
            name="st_averageAnnualIncome"
            value={formData.st_averageAnnualIncome}
            onChange={handleInputChange}
            placeholder="ระบุรายได้เฉลี่ย"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">สถานะบิดามารดา</Form.Label>
        <div className="col-sm-4">
          <Form.Check
            inline
            type="radio"
            label="อยู่ด้วยกัน"
            name="st_parentStatus"
            value="togather"
            checked={formData.st_parentStatus === 'togather'}
            onChange={handleInputChange}
          />
          <Form.Check
            inline
            type="radio"
            label="แยกกันอยู่"
            name="st_parentStatus"
            value="separated"
            checked={formData.st_parentStatus === 'separated'}
            onChange={handleInputChange}
          />
          <Form.Check
            inline
            type="radio"
            label="หย่าร้าง"
            name="st_parentStatus"
            value="divorce"
            checked={formData.st_parentStatus === 'divorce'}
            onChange={handleInputChange}
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ผู้ปกครอง</Form.Label>
        <div className="col-sm-4">
          <Form.Check
            inline
            type="radio"
            label="บิดา"
            name="st_parent"
            value="dad"
            checked={formData.st_parent === 'dad'}
            onChange={() => handleParentSelection('dad')}
          />
          <Form.Check
            inline
            type="radio"
            label="มารดา"
            name="st_parent"
            value="mom"
            checked={formData.st_parent === 'mom'}
            onChange={() => handleParentSelection('mom')}
          />
          <Form.Check
            inline
            type="radio"
            label="อื่นๆ"
            name="st_parent"
            value="other"
            checked={formData.st_parent === 'other'}
            onChange={() => handleParentSelection('other')}
          />
        </div>
        <Form.Label className="col-sm-2">ความเกี่ยวข้อง</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentRelation"
            value={formData.st_parentRelation}
            onChange={handleInputChange}
            placeholder="ความเกี่ยวข้อง"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">เลขประจำตัว ผู้ปกครอง</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentIdCard"
            value={formData.st_parentIdCard}
            onChange={handleInputChange}
            placeholder="เลขประจำตัว"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">วัน เดือน ปี เกิด</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_parentBirthdate"
            value={formData.st_parentBirthdate}
            onChange={handleInputChange}
            placeholder="เช่น 22/07/2550"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ชื่อ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_parentname"
            value={formData.st_parentname}
            onChange={handleInputChange}
            placeholder="ชื่อ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">อาชีพ</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="text"
            name="st_parentjob"
            value={formData.st_parentjob}
            onChange={handleInputChange}
            placeholder="อาชีพ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">รายได้/ปี</Form.Label>
        <div className="col-sm-2">
          <Form.Control
            type="number"
            name="st_parentannualincome"
            value={formData.st_parentannualincome}
            onChange={handleInputChange}
            placeholder="รายได้"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ที่อยู่</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            as="textarea"
            rows={3}
            name="st_parentAddress"
            value={formData.st_parentAddress}
            onChange={handleInputChange}
            placeholder="ที่อยู่"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">ตำบล</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentSubDistrict"
            value={formData.st_parentSubDistrict}
            onChange={handleInputChange}
            placeholder="ตำบล"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อำเภอ</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentDistrict"
            value={formData.st_parentDistrict}
            onChange={handleInputChange}
            placeholder="อำเภอ"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">จังหวัด</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentProvince"
            value={formData.st_parentProvince}
            onChange={handleInputChange}
            placeholder="จังหวัด"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">รหัสไปรษณีย์</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentZipcode"
            value={formData.st_parentZipcode}
            onChange={handleInputChange}
            placeholder="รหัสไปรษณีย์"
          />
        </div>
        <Form.Label className="col-sm-2 text-end">เบอร์โทร</Form.Label>
        <div className="col-sm-4">
          <Form.Control
            type="text"
            name="st_parentPhone"
            value={formData.st_parentPhone}
            onChange={handleInputChange}
            placeholder="เบอร์โทรศัพท์"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">ผู้ออกค่าใช้จ่าย</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_educationsupporter"
            value={formData.st_educationsupporter}
            onChange={handleInputChange}
            placeholder="ชื่อผู้ออกค่าใช้จ่าย"
          />
        </div>
      </Form.Group>

      {/* ส่วนที่ 7: ข้อมูลอื่น */}
      <hr className="my-4" />
      <h5 className="mb-3">ข้อมูลอื่นๆ</h5>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">กีฬาที่สนใจ</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_favoritesport"
            value={formData.st_favoritesport}
            onChange={handleInputChange}
            placeholder="ระบุกีฬา"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">วิชาที่ชอบ</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_favoritesubject"
            value={formData.st_favoritesubject}
            onChange={handleInputChange}
            placeholder="ระบุวิชา"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">แนวทางการศึกษาต่อ</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_futurestudy"
            value={formData.st_futurestudy}
            onChange={handleInputChange}
            placeholder="ระบุแนวทาง"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3">
        <Form.Label className="col-sm-2 text-end">อาชีพในอนาคต</Form.Label>
        <div className="col-sm-3">
          <Form.Control
            type="text"
            name="st_futurejob"
            value={formData.st_futurejob}
            onChange={handleInputChange}
            placeholder="ระบุแนวทางการประกอบอาชีพในอนาคต"
          />
        </div>
      </Form.Group>

      <Form.Group className="row mb-3 text-center">
        <div className="col-sm-offset-2 col-sm-10">
          <Button type="submit" variant="success">
            บันทึกข้อมูล
          </Button>
        </div>
      </Form.Group>
    </Form>
  );
}
